<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Payroll Cycles"
      text="Update payroll cycle"
      class="mb-0"
      :loading="loading"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row>
            <v-col
              cols="12"
              class="py-0"
            >
              <v-text-field
                v-model="localPayrollCycle.name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"    
                data-vv-name="name"
                data-vv-as="name"
                label="Name"
                outlined
              />
            </v-col>

            <v-col cols="12">
              <tiptap-vuetify
                v-model="localPayrollCycle.description"
                class="full-width text-editor-toolbar-height pb-1"
                placeholder="Description"
                :extensions="extensions"
              />
            </v-col>

            <v-col cols="12">
              <span>Include Additional Deduction (TP1)? </span>
              <v-radio-group
                v-model="localPayrollCycle.include_tp_one"
                class="mt-0"
                row
              >
                <v-radio
                  label="Yes"
                  :value="1"
                /> 
                <v-radio
                  label="No"
                  :value="0"
                />
              </v-radio-group>
            </v-col>

            <v-col cols="12">
              <span>Include Basic Salary? </span>
              <v-radio-group
                v-model="localPayrollCycle.include_basic_salary"
                class="mt-0"
                row
                @change="setDefaultValue"
              >
                <v-radio
                  label="Yes"
                  :value="1"
                /> 
                <v-radio
                  label="No"
                  :value="0"
                />
              </v-radio-group>
            </v-col>

            <v-col
              v-if="localPayrollCycle.include_basic_salary"
              cols="8"
              class="py-0"
            >
              <v-autocomplete
                v-model="localPayrollCycle.start_period" 
                v-validate="'required'"
                label="Start Period"
                item-text="name"
                item-value="name"
                :error-messages="errors.collect('start_period')"    
                data-vv-name="start_period" 
                data-vv-as="start period"
                :items="timePeriods"
                outlined
              />
            </v-col>

            <v-col
              v-if="localPayrollCycle.include_basic_salary"
              cols="4"
              class="py-0"
            >
              <v-text-field
                v-model="localPayrollCycle.start_day" 
                :rules="[rules.startDayRequired,rules.min,rules.max]"
                label="Start Day"
                type="number"
                outlined
              />
            </v-col>

            <v-col
              v-if="localPayrollCycle.include_basic_salary"
              cols="8"
              class="py-0"
            >
              <v-autocomplete
                v-model="localPayrollCycle.end_period" 
                v-validate="'required'"
                :error-messages="errors.collect('end_period')"    
                data-vv-name="end_period" 
                data-vv-as="end period"
                label="End Period"
                item-text="name"
                item-value="name"
                :items="timePeriods"
                outlined
              />
            </v-col>

            <v-col
              v-if="localPayrollCycle.include_basic_salary"
              cols="4"
              class="py-0"
            >
              <v-text-field
                v-model="localPayrollCycle.end_day" 
                :rules="[rules.endDayRequired,rules.min,rules.max]"
                label="End Day"
                type="number"
                outlined
              />
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"      
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions> 
    </material-card>  
  </v-dialog>
</template>

<script>
  import { TiptapVuetify, Heading, Bold, Italic, Underline, BulletList, OrderedList, ListItem, Link, HardBreak } from 'tiptap-vuetify'

  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    components: {
      TiptapVuetify,
    },
    props: {
      payrollCycle: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        localPayrollCycle: null,
        loading: false,
        extensions: [
          Link,
          Underline,
          Italic,
          ListItem,
          BulletList,
          OrderedList,
          [Heading, {
            options: {
              levels: [1, 2, 3],
            },
          }],
          Bold,
          HardBreak,
        ],
        timePeriods: [
          { name: 'current_month' }, 
          // { name: 'previous_month' },
        ],
        rules: {
          startDayRequired: value => !!value || 'The start day field is required',     
          endDayRequired: value => !!value || 'The end day field is required',     
          min: value => value >= 1 || 'Minimum number is 1',      
          max: value => value <= 31 || 'Maximum number is 31',      
        },  
      }
    },
    watch: {
      dialog: function (value) {
        if (!value) return

        this.loading = true

        this.localPayrollCycle = { ...this.payrollCycle }

        this.loading = false
      },
    },
    methods: { 
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.setProgressing(true)

            this.$api.hrisPayrollCycles().update(this.payrollCycle.id, this.prepareFormData())
              .then(response => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })

                  this.setProgressing(false)
                  this.dialog = false
                }
              })
              .catch(() => {
                this.setProgressing(false)
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },
      prepareFormData () {
        return {
          name: this.localPayrollCycle.name,
          description: this.localPayrollCycle.description,
          start_period: this.localPayrollCycle.start_period,
          start_day: this.localPayrollCycle.start_day,
          end_period: this.localPayrollCycle.end_period,
          end_day: this.localPayrollCycle.end_day,
          include_basic_salary: this.localPayrollCycle.include_basic_salary,
          include_tp_one: this.localPayrollCycle.include_tp_one,
        }
      },

      setDefaultValue () {
        if (!this.localPayrollCycle.include_basic_salary) {
          this.localPayrollCycle.start_period = null
          this.localPayrollCycle.start_day = 1        
          this.localPayrollCycle.end_period = null
          this.localPayrollCycle.end_day = 31
        }
      },
    },
  }
</script>

<style scoped>
.text-editor-toolbar-height>>> .v-toolbar__content{
  height: unset !important;
  min-height: 42px;
}
.text-editor-toolbar-height>>>.v-sheet.v-sheet--tile.theme--light.v-toolbar.v-toolbar--dense.v-toolbar--flat.grey.lighten-4{
  min-height:unset !important;
}
.full-width{
  width: 100%;
}
</style>
